import { combineReducers } from "redux";
import { types } from "../actions";
import { clamp } from "lodash-es";
import { MAX_QUANTITY } from "../../constants";

const qty = (state = 1, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_QTY:
      return clamp(Math.round(payload), 1, MAX_QUANTITY);
    case types.RESET:
    case types.RESET_PRODUCT:
      return 1;
    default:
      return state;
  }
};

const formatSelections = selection => ({
  name: selection.product.name,
  productId: selection.product_id,
  sku: selection.product.sku,
  selectionId: selection.selection_id,
  price: Number(selection.product.price),
  targetPrice: Number(selection.targetPrice),
  financeType: selection.financeType,
  qty: selection.selection_qty,
  inventory_qty: selection.product.inventory_qty,
  excludeFromMarginSplit: selection.exclude_from_margin_split,
  includesMainUnit: selection.includes_main_unit,
  isHardware: selection.is_hardware,
  isSoftware: selection.is_software,
  isSaas: selection.is_saas,
  additionalLinkedPart: (selection.product.additional_linked_part || "")
    .trim()
    .split(",")
    .filter(Boolean)
});

const addLinkedPartToSelections = (pickedOptions, allOptions) => {
  // Make a object that stores Linked skus and quantity {sku1: 1, sku2: 2}
  const qtyOfLinkedPart = {};
  pickedOptions.forEach(o =>
    o.selections.forEach(s => {
      s.additionalLinkedPart.forEach(
        sku =>
          (qtyOfLinkedPart[sku] = qtyOfLinkedPart.hasOwnProperty(sku)
            ? qtyOfLinkedPart[sku] + s.qty
            : s.qty)
      );
    })
  );

  // Stores option ids of selections of linked part
  const optionIdsOfLinkedPart = [];

  // Get linked part selection objects
  const linkedPartSelections = allOptions
    .map(o => ({
      ...o,
      selections: o.selections.filter(s => {
        const isLinked = Object.keys(qtyOfLinkedPart).includes(s.product.sku);
        if (isLinked) {
          s.selection_qty = qtyOfLinkedPart[s.product.sku] ?? 1;
        }
        return isLinked;
      })
    }))
    .filter(o => {
      if (o.selections.length > 0) {
        optionIdsOfLinkedPart.push(o.option_id);
        return true;
      }
      return false;
    });

  // Merge linked selections with pickedOptions
  const mergedState = pickedOptions.concat(
    linkedPartSelections.filter(
      o => !optionIdsOfLinkedPart.includes(o.option_id)
    )
  );

  // Format data
  return mergedState.map(o => {
    const item = linkedPartSelections.find(lps => lps.option_id === o.optionId);

    if (item) {
      const data = {
        optionId: o.option_id,
        optionName: o.title,
        required: o.required,
        selections: item.selections.map(is => formatSelections(is))
      };
      const dataSelectionIds = data.selections.map(ds => ds.selectionId);
      o = {
        ...o,
        selections: o.selections
          .filter(os => !dataSelectionIds.includes(os.selectionId))
          .concat(data.selections)
      };
    }
    return o;
  });
};

function removeLinkedPartFromSelections(selectedOptions, allOptions) {
  //get linked product skus with their parent
  const allLinkedSkus = [
    ...new Set(
      allOptions
        .flatMap(o => o.selections)
        .flatMap(s =>
          (s.product.additional_linked_part || "")
            .split(",")
            .filter(s => s.trim() !== "")
            .map(so => ({ sku: so, parent: s.product.sku }))
        )
    )
  ];
  //get all selected skus
  const selectedOptionsSkus = selectedOptions.flatMap(o =>
    o.selections.map(s => s.sku)
  );
  //if the parent sku is not selected, link product will not be reemoved
  const filteredLinkedSkus = allLinkedSkus
    .filter(als => selectedOptionsSkus.includes(als.parent))
    .map(fls => fls.sku);
  // Remove all linked part from current state
  return selectedOptions.map(o => ({
    ...o,
    selections: o.selections.filter(s => !filteredLinkedSkus.includes(s.sku))
  }));
}

// 1. Remove linked part selections from the current state
// 2. Get current picked selections
// 3. Add linked part selections to the picked selections
const pickedOptionSelections = (state = [], { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_PICKED_OPTION_SELECTIONS: {
      const { selectedOptions, allOptions } = payload;
      // Remove all linked part from current state
      const pickedSelectionsWithoutLinkedPart = removeLinkedPartFromSelections(
        selectedOptions,
        allOptions
      );
      return addLinkedPartToSelections(
        pickedSelectionsWithoutLinkedPart,
        allOptions
      );
    }
    case types.ADD_PRODUCT_SELECTION: {
      const { option, selection, allOptions } = payload;

      // Remove all linked part from current state
      const selectionsWithoutLinkedPart = removeLinkedPartFromSelections(
        state,
        allOptions
      );

      // If it's a "radio" button or "select", reset the selections first.
      // Possible option type: select, radio, checkbox, multi.
      const singleValue = ["select", "radio"].includes(option.type);
      const thePickedSelections = singleValue
        ? []
        : state
            .filter(o => o.optionId === option.option_id)
            .flatMap(o => o.selections);

      const pickedSelectionsWithoutLinkedPart = selectionsWithoutLinkedPart
        .filter(o => o.optionId !== option.option_id)
        .concat([
          {
            optionId: option.option_id,
            optionName: option.title,
            required: option.required,
            selections: thePickedSelections
              .filter(s => s.selectionId !== selection.selection_id)
              .concat([formatSelections(selection)])
          }
        ]);

      return addLinkedPartToSelections(
        pickedSelectionsWithoutLinkedPart,
        allOptions
      );
    }
    case types.DELETE_PRODUCT_SELECTION: {
      // Remove the selection
      const { option, selection, allOptions } = payload;

      // Remove all linked part from current state
      const selectionsWithoutLinkedPart = removeLinkedPartFromSelections(
        state,
        allOptions
      );

      const pickedSelectionsWithoutLinkedPart = selectionsWithoutLinkedPart
        .map(o =>
          o.optionId === option.option_id
            ? {
                ...o,
                selections: o.selections.filter(
                  // Keep other selections;
                  // Keep the selection to be deleted if it belongs to
                  // a required option and it is the last one
                  s =>
                    s.selectionId !== selection.selection_id ||
                    (o.required === "1" && o.selections.length === 1)
                )
              }
            : o
        )
        .filter(
          // Removed the empty option
          o => o.selections.length > 0
        );

      return addLinkedPartToSelections(
        pickedSelectionsWithoutLinkedPart,
        allOptions
      );
    }
    case types.UPDATE_PRODUCT_SELECTION_QTY: {
      // Update the quantity of matching picked option selection
      const { allOptions } = payload;

      // Remove all linked part from current state
      const selectionsWithoutLinkedPart = removeLinkedPartFromSelections(
        state,
        allOptions
      );

      const pickedSelectionsWithoutLinkedPart = selectionsWithoutLinkedPart.map(
        o =>
          o.optionId === payload.optionId
            ? {
                ...o,
                selections: o.selections.map(s =>
                  s.selectionId === payload.selectionId
                    ? { ...s, qty: payload.qty }
                    : s
                )
              }
            : o
      );

      return addLinkedPartToSelections(
        pickedSelectionsWithoutLinkedPart,
        allOptions
      );
    }
    case types.UPDATE_PRODUCT_SELECTION_TARGET_PRICE: {
      // Update the quantity of matching picked option selection
      const { allOptions } = payload;

      // Remove all linked part from current state
      const selectionsWithoutLinkedPart = removeLinkedPartFromSelections(
        state,
        allOptions
      );

      const pickedSelectionsWithoutLinkedPart = selectionsWithoutLinkedPart.map(
        o =>
          o.optionId === payload.optionId
            ? {
                ...o,
                selections: o.selections.map(s =>
                  s.selectionId === payload.selectionId
                    ? { ...s, targetPrice: Number(payload.targetPrice) }
                    : s
                )
              }
            : o
      );

      return addLinkedPartToSelections(
        pickedSelectionsWithoutLinkedPart,
        allOptions
      );
    }
    case types.UPDATE_PRODUCT_SELECTION_FINANCE_TYPE: {
      // Update the quantity of matching picked option selection
      const { allOptions } = payload;

      // Remove all linked part from current state
      const selectionsWithoutLinkedPart = removeLinkedPartFromSelections(
        state,
        allOptions
      );

      const pickedSelectionsWithoutLinkedPart = selectionsWithoutLinkedPart.map(
        o =>
          o.optionId === payload.optionId
            ? {
                ...o,
                selections: o.selections.map(s =>
                  s.selectionId === payload.selectionId
                    ? { ...s, financeType: payload.financeType }
                    : s
                )
              }
            : o
      );

      return addLinkedPartToSelections(
        pickedSelectionsWithoutLinkedPart,
        allOptions
      );
    }
    case types.SET_PRODUCT_SAAS_FINANCE_TYPE:
      return state.map(o => {
        return {
          ...o,
          selections: o.selections.map(s =>
            s.isSaas ? { ...s, financeType: payload } : s
          )
        };
      });

    case types.SET_QUOTE_FINANCE_TYPE: {
      return state.map(o => {
        return {
          ...o,
          selections: o.selections.map(s =>
            s.isSaas ? s : { ...s, financeType: payload }
          )
        };
      });
    }

    case types.RESET:
    case types.RESET_PRODUCT:
      return [];
    default:
      return state;
  }
};

// The fields in availableOptions follow the snake_case.
// E.g. option_id, selection_id, selection_qty
const availableOptions = (state = [], { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_AVAILABLE_OPTIONS:
      return payload;
    case types.UPDATE_PRODUCT_SELECTION_QTY:
      // Update the quantity in available options
      return state.map(o =>
        o.option_id === payload.optionId
          ? {
              ...o,
              selections: o.selections.map(s =>
                s.selection_id === payload.selectionId
                  ? { ...s, selection_qty: payload.qty }
                  : s
              )
            }
          : o
      );
    case types.UPDATE_PRODUCT_SELECTION_TARGET_PRICE:
      // Update the target price in available options
      return state.map(o =>
        o.option_id === payload.optionId
          ? {
              ...o,
              selections: o.selections.map(s =>
                s.selection_id === payload.selectionId
                  ? { ...s, targetPrice: Number(payload.targetPrice) }
                  : s
              )
            }
          : o
      );
    case types.UPDATE_PRODUCT_SELECTION_FINANCE_TYPE:
      // Update the target price in available options
      return state.map(o =>
        o.option_id === payload.optionId
          ? {
              ...o,
              selections: o.selections.map(s =>
                s.selection_id === payload.selectionId
                  ? { ...s, financeType: payload.financeType }
                  : s
              )
            }
          : o
      );
    case types.SET_PRODUCT_SAAS_FINANCE_TYPE:
      return state.map(o => {
        return {
          ...o,
          selections: o.selections.map(s =>
            s.is_saas ? { ...s, financeType: payload } : s
          )
        };
      });
    case types.SET_QUOTE_FINANCE_TYPE:
      return state.map(o => {
        return {
          ...o,
          selections: o.selections.map(s =>
            s.is_saas ? s : { ...s, financeType: payload }
          )
        };
      });
    case types.RESET:
    case types.RESET_PRODUCT:
      return [];
    default:
      return state;
  }
};

const customAttributes = (state = [], { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_CUSTOM_ATTRIBUTES:
      return payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return [];
    default:
      return state;
  }
};

const subscriptionTargetChargeValue = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_SUBSCRIPTION_TARGET_CHARGE_VALUE:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const subscriptionUserNum = (state = 1, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_SUBSCRIPTION_USER_NUM:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 1;
    default:
      return state;
  }
};

const subscriptionTerm = (state = 12, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_SUBSCRIPTION_TERM:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 12;
    default:
      return state;
  }
};

const ccBw = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_CC_BW:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const ccColour = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_CC_COLOUR:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const scanCharge = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_SCAN_CHARGE:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const defaultCcBw = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_DEFAULT_CC_BW:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const defaultCcColour = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_DEFAULT_CC_COLOUR:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const userClickVolumeBw = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_USER_CLICK_VOLUME_BW:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const userClickVolumeColour = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_USER_CLICK_VOLUME_COLOUR:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const defaultScanCharge = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_DEFAULT_SCAN_CHARGE:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const hasFallbackCcBw = (state = false, { type, payload }) => {
  switch (type) {
    case types.SET_HAS_FALLBACK_CC_BW:
      return Boolean(payload) || false;
    case types.RESET:
    case types.RESET_PRODUCT:
      return false;
    default:
      return state;
  }
};

const hasFallbackCcColour = (state = false, { type, payload }) => {
  switch (type) {
    case types.SET_HAS_FALLBACK_CC_COLOUR:
      return Boolean(payload) || false;
    case types.RESET:
    case types.RESET_PRODUCT:
      return false;
    default:
      return state;
  }
};

const msaMetroClickChargeBw = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_METRO_CC_BW:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const msaMetroClickChargeColour = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_METRO_CC_COLOUR:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const msaRegionalClickChargeBw = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_REGIONAL_CC_BW:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const msaRegionalClickChargeColour = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_REGIONAL_CC_COLOUR:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const scanningMonthlyCharge = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_SCANNING_MONTHLY_CHARGE:
      console.log(types.SET_PRODUCT_SCANNING_MONTHLY_CHARGE, state);
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};
const defaultScanningMonthlyCharge = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_DEFAULT_SCANNING_MONTHLY_CHARGE:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const nzScanningMonthlyCharge = (state = null, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_NZ_SCANNING_MONTHLY_CHARGE:
      console.log(types.SET_PRODUCT_NZ_SCANNING_MONTHLY_CHARGE, state);
      return payload === undefined ? null : payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return null;
    default:
      return state;
  }
};
const defaultNzScanningMonthlyCharge = (state = [0], { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_DEFAULT_NZ_SCANNING_MONTHLY_CHARGE:
      return payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return [0];
    default:
      return state;
  }
};

const flatMonthlyCharge = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_FLAT_MONTHLY_CHARGE:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};
const defaultFlatMonthlyCharge = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_DEFAULT_FLAT_MONTHLY_CHARGE:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const flatMonthlyChargeModifiable = (state = false, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_FLAT_MONTHLY_CHARGE_MODIFIABLE:
      return payload === "1"; //note, value is sent from Magento as "1" (string)
    case types.RESET:
    case types.RESET_PRODUCT:
      return false;
    default:
      return state;
  }
};

const campaigns = (state = [], { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_CAMPAIGN:
      return state.map(c => (c.name === payload.name ? payload : c));
    case types.ADD_PRODUCT_CAMPAIGN:
      return [
        ...state.filter(({ name }) => name !== payload.isSelected),
        payload
      ];
    case types.RESET:
    case types.RESET_PRODUCT:
      return [];
    default:
      return state;
  }
};

const msaTerm = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_TERM:
      return Math.round(Number(payload)) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const msaDefaultTerm = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_DEFAULT_TERM:
      return Math.round(Number(payload)) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const msaCfa = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_CFA:
      return Math.round(payload * 100) / 100 || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const msaDefaultCfa = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_DEFAULT_CFA:
      return Number(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const msaMdsCost = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_MDS_COST:
      return Math.round(Math.abs(payload) * 100) / 100 || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const msaDefaultMdsCost = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_DEFAULT_MDS_COST:
      return Math.abs(payload) || 0;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};

const msaPriceFlag = (state = "", { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_PRICE_FLAG:
      return payload || "";
    case types.RESET:
    case types.RESET_PRODUCT:
      return "";
    default:
      return state;
  }
};

const msaPayableMethod = (state = "", { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_PAYABLE_METHOD:
      return ["ADVANCE", "ARREARS"].includes(payload) ? payload : "";
    case types.RESET:
    case types.RESET_PRODUCT:
      return "";
    default:
      return state;
  }
};

const msaInstalmentFrequency = (state = "", { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MSA_INSTALMENT_FREQUENCY:
      return ["MONTHLY", "QUARTERLY", "YEARLY"].includes(payload)
        ? payload
        : "";
    case types.RESET:
    case types.RESET_PRODUCT:
      return "";
    default:
      return state;
  }
};

const minimumMonthlyCharge = (state = null, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MINIMUM_MONTHLY_CHARGE:
      return payload === undefined ? null : payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return null;
    default:
      return state;
  }
};
const defaultMinimumMonthlyCharge = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_DEFAULT_MINIMUM_MONTHLY_CHARGE:
      return payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return 0;
    default:
      return state;
  }
};
const minimumMonthlyChargeModifiable = (state = false, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_MINIMUM_MONTHLY_CHARGE_MODIFIABLE:
      return payload === "1"; //note, value is sent from Magento as "1" (string)
    case types.RESET:
    case types.RESET_PRODUCT:
      return false;
    default:
      return state;
  }
};

const tonerDeliveryCharge = (state = null, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_TONER_DELIVERY_CHARGE:
      return payload === undefined ? null : payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return null;
    default:
      return state;
  }
};
const defaultTonerDeliveryCharge = (state = null, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_DEFAULT_TONER_DELIVERY_CHARGE:
      return payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return null;
    default:
      return state;
  }
};
const perA3Charge = (state = null, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_PER_A3_CHARGE:
      return payload === undefined ? null : payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return null;
    default:
      return state;
  }
};
const defaultPerA3Charge = (state = null, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_DEFAULT_PER_A3_CHARGE:
      return payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return [0];
    default:
      return state;
  }
};

const saasFinanceType = (state = null, { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_SAAS_FINANCE_TYPE:
      return payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return "Cash";
    default:
      return state;
  }
};

const saasExistingTenant = (state = "", { type, payload }) => {
  switch (type) {
    case types.SET_PRODUCT_SAAS_EXISTING_TENANT:
      return payload;
    case types.RESET:
    case types.RESET_PRODUCT:
      return "";
    default:
      return state;
  }
};

export default combineReducers({
  qty,

  pickedOptionSelections,
  availableOptions,
  customAttributes,

  subscriptionTargetChargeValue,
  subscriptionUserNum,
  subscriptionTerm,

  hasFallbackCcBw,
  defaultCcBw,
  ccBw,
  userClickVolumeBw,

  hasFallbackCcColour,
  defaultCcColour,
  ccColour,
  userClickVolumeColour,

  defaultScanCharge,
  scanCharge,

  scanningMonthlyCharge,
  defaultScanningMonthlyCharge,

  nzScanningMonthlyCharge,
  defaultNzScanningMonthlyCharge,

  defaultFlatMonthlyCharge,
  flatMonthlyCharge,
  flatMonthlyChargeModifiable,

  campaigns,

  msaDefaultTerm,
  msaTerm,

  msaDefaultCfa,
  msaCfa,

  msaDefaultMdsCost,
  msaMdsCost,

  msaPriceFlag,
  msaPayableMethod,
  msaInstalmentFrequency,

  msaMetroClickChargeBw,
  msaMetroClickChargeColour,
  msaRegionalClickChargeBw,
  msaRegionalClickChargeColour,

  defaultMinimumMonthlyCharge,
  minimumMonthlyCharge,
  minimumMonthlyChargeModifiable,

  defaultTonerDeliveryCharge,
  tonerDeliveryCharge,

  defaultPerA3Charge,
  perA3Charge,

  saasFinanceType,
  saasExistingTenant
});
